import { sanitizeAnalyticsProperties } from "./payload";
import { amplitudeProvider } from "./providers/amplitude";
import { postHogProvider } from "./providers/posthog";
import { AnalyticsEvent, AnalyticsIdentity, AnalyticsInitConfig, AnalyticsProperties, AnalyticsProvider } from "./types";
import { scheduleBackgroundTask } from "../scheduleBackgroundTask";

const providers: AnalyticsProvider[] = [amplitudeProvider, postHogProvider];
let initialized = false;
const warnedProviders = new Set<string>();

const schedule = (operation: () => void, delay = 0) => {
  const run = () => {
    try {
      operation();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("Analytics operation failed", error);
      }
    }
  };

  scheduleBackgroundTask(run, { delay });
};

const warnOnce = (provider: AnalyticsProvider) => {
  if (!import.meta.env.DEV || warnedProviders.has(provider.name)) {
    return;
  }
  warnedProviders.add(provider.name);
  console.warn(`${provider.name} analytics provider is not initialized yet`);
};

export const analyticsClient = {
  init(config: AnalyticsInitConfig) {
    initialized = true;
    schedule(() => providers.forEach((provider) => provider.init(config)));
  },
  track(event: AnalyticsEvent) {
    schedule(() => {
      if (!initialized) {
        providers.forEach(warnOnce);
      }
      providers.forEach((provider) => provider.track(event));
    });
  },
  identify(identity: AnalyticsIdentity) {
    schedule(() => providers.forEach((provider) => provider.identify({ ...identity, traits: sanitizeAnalyticsProperties(identity.traits || {}) })));
  },
  setUserProperties(properties: AnalyticsProperties) {
    const sanitized = sanitizeAnalyticsProperties(properties);
    schedule(() => providers.forEach((provider) => provider.setUserProperties(sanitized)));
  },
  reset() {
    schedule(() => providers.forEach((provider) => provider.reset()));
  }
};
